import React from 'react';
import { createPortal } from 'react-dom';

const toCssValue = (value) => (typeof value === 'number' ? `${value}px` : value);
const clamp = (value, min, max) => Math.min(Math.max(value, min), max);

const TableHoverCard = ({
    show,
    index = 0,
    placement = 'right',
    cardWidth = 320,
    popupHeight = 320,
    offsetX = 12,
    offsetY = 8,
    topOffset = 0,
    bottomOffset = 0,
    anchorRect = null,
    portalToBody = false,
    viewportPadding = 12,
    zIndex = 10000,
    onMouseEnter,
    onMouseLeave,
    children,
}) => {
    if (!show) return null;

    const viewportHeight =
        typeof document !== 'undefined'
            ? document.documentElement.clientHeight
            : 0;

    if (
        portalToBody &&
        anchorRect &&
        typeof document !== 'undefined' &&
        typeof window !== 'undefined'
    ) {
        const viewportWidth = document.documentElement.clientWidth || 0;
        const spaceOnRight = viewportWidth - anchorRect.right - viewportPadding;
        const spaceOnLeft = anchorRect.left - viewportPadding;

        const shouldPlaceLeft =
            placement === 'left'
                ? true
                : spaceOnRight < cardWidth && spaceOnLeft > spaceOnRight;

        const preferredLeft = shouldPlaceLeft
            ? anchorRect.left - cardWidth - offsetX
            : anchorRect.right + offsetX;
        // Keep the card inside the viewport instead of letting it run off the edge.
        const left = clamp(
            preferredLeft,
            viewportPadding,
            Math.max(viewportWidth - cardWidth - viewportPadding, viewportPadding)
        );

        const preferredTop = anchorRect.top + topOffset + offsetY;
        const wouldOverflowBottom =
            preferredTop + popupHeight > viewportHeight - viewportPadding;
        const top = wouldOverflowBottom
            ? anchorRect.top - bottomOffset
            : clamp(
                preferredTop,
                viewportPadding,
                Math.max(viewportHeight - popupHeight - viewportPadding, viewportPadding)
            );
        const transform = wouldOverflowBottom ? 'translateY(-100%)' : undefined;

        return createPortal(
            <div
                style={{
                    position: 'fixed',
                    zIndex,
                    background: 'transparent',
                    left: toCssValue(left),
                    top: toCssValue(top),
                    transform,
                }}
                onMouseEnter={onMouseEnter}
                onMouseLeave={onMouseLeave}
            >
                {children}
            </div>,
            document.body
        );
    }

    const availableSpaceAbove = anchorRect
        ? anchorRect.top - viewportPadding
        : index * popupHeight;
    const availableSpaceBelow = anchorRect
        ? viewportHeight - anchorRect.top - viewportPadding
        : viewportHeight - (availableSpaceAbove + popupHeight);

    const verticalStyle =
        availableSpaceBelow >= popupHeight || availableSpaceBelow > availableSpaceAbove
            ? { top: toCssValue(topOffset) }
            : { bottom: toCssValue(bottomOffset) };

    const horizontalStyle =
        placement === 'left'
            ? { left: `-${cardWidth + offsetX}px` }
            : { left: `calc(100% + ${offsetX}px)` };

    return (
        <div
            style={{
                position: 'absolute',
                zIndex,
                background: 'transparent',
                ...horizontalStyle,
                ...verticalStyle,
                transform: offsetY ? `translateY(${toCssValue(offsetY)})` : undefined,
            }}
            onMouseEnter={onMouseEnter}
            onMouseLeave={onMouseLeave}
        >
            {children}
        </div>
    );
};

export default TableHoverCard;
